import { FC, useCallback, useMemo, useState } from "react";
import { PaymentMethodeEnum, MobilePaymentEnum } from '@utilities/enums';
import { Client, MultiplePaymentMethod } from '@utilities/types';
import { Button, Input, InputWrapper } from "@components";
import { formatPrice } from "@utilities/functions";
import { Plus, Trash2, CreditCard, Banknote, Smartphone, FileText, Wallet } from "lucide-react";

interface Props {
    totalToPay: number;
    paymentMethods: MultiplePaymentMethod[];
    selectedClient: Client | null;
    onAddPayment: (payment: MultiplePaymentMethod) => void;
    onRemovePayment: (index: number) => void;
    onUpdatePayment: (index: number, payment: MultiplePaymentMethod) => void;
}

const getPaymentIcon = (methode: PaymentMethodeEnum) => {
    switch (methode) {
        case PaymentMethodeEnum.esp:
            return <Banknote className="w-4 h-4" />;
        case PaymentMethodeEnum.mobilMoney:
            return <Smartphone className="w-4 h-4" />;
        case PaymentMethodeEnum.cb:
            return <CreditCard className="w-4 h-4" />;
        case PaymentMethodeEnum.cheque:
            return <FileText className="w-4 h-4" />;
        case PaymentMethodeEnum.credit:
            return <Wallet className="w-4 h-4" />;
        default:
            return <CreditCard className="w-4 h-4" />;
    }
};

const getPaymentMethodColor = (methode: PaymentMethodeEnum) => {
    switch (methode) {
        case PaymentMethodeEnum.esp:
            return 'bg-green-100 border-green-300 text-green-800';
        case PaymentMethodeEnum.mobilMoney:
            return 'bg-orange-100 border-orange-300 text-orange-800';
        case PaymentMethodeEnum.cb:
            return 'bg-blue-100 border-blue-300 text-blue-800';
        case PaymentMethodeEnum.cheque:
            return 'bg-purple-100 border-purple-300 text-purple-800';
        case PaymentMethodeEnum.credit:
            return 'bg-red-100 border-red-300 text-red-800';
        default:
            return 'bg-gray-100 border-gray-300 text-gray-800';
    }
};

export const MultiPaymentSelector: FC<Props> = ({
    totalToPay,
    paymentMethods,
    selectedClient,
    onAddPayment,
    onRemovePayment,
    onUpdatePayment,
}) => {
    const [selectedMethode, setSelectedMethode] = useState<PaymentMethodeEnum | null>(null);
    const [selectedMobileMode, setSelectedMobileMode] = useState<MobilePaymentEnum | null>(null);
    const [amount, setAmount] = useState<number>(0);
    const [transactionRef, setTransactionRef] = useState<string>('');
    const [notes, setNotes] = useState<string>('');

    // Calcul du total déjà payé
    const totalPaid = useMemo(() =>
        paymentMethods.reduce((sum, p) => sum + p.amount, 0),
        [paymentMethods]);

    // Montant restant à payer
    const remainingAmount = useMemo(() =>
        Math.max(0, totalToPay - totalPaid),
        [totalToPay, totalPaid]);

    // Modes de paiement disponibles
    const modesToShow = useMemo(() => (
        Object.entries(PaymentMethodeEnum).filter(([key, value]) => {
            if (value === PaymentMethodeEnum.credit && !selectedClient) return false;
            return true;
        })
    ), [selectedClient]);

    const mobileModes = useMemo(() => Object.entries(MobilePaymentEnum), []);

    const isMobilePayment = useMemo(() =>
        selectedMethode === PaymentMethodeEnum.mobilMoney,
        [selectedMethode]);

    const isCreditPayment = useMemo(() =>
        selectedMethode === PaymentMethodeEnum.credit,
        [selectedMethode]);

    // Vérifier si on peut ajouter ce paiement
    const canAddPayment = useMemo(() => {
        if (!selectedMethode || amount <= 0) return false;
        if (isMobilePayment && !selectedMobileMode) return false;
        if (amount > remainingAmount) return false;
        return true;
    }, [selectedMethode, amount, isMobilePayment, selectedMobileMode, remainingAmount]);

    // Réinitialiser le formulaire
    const resetForm = useCallback(() => {
        setSelectedMethode(null);
        setSelectedMobileMode(null);
        setAmount(0);
        setTransactionRef('');
        setNotes('');
    }, []);

    // Ajouter un paiement
    const handleAddPayment = useCallback(() => {
        if (!canAddPayment || !selectedMethode) return;

        const newPayment: MultiplePaymentMethod = {
            methode: selectedMethode,
            amount,
            ...(isMobilePayment && selectedMobileMode && { mobilePayment: selectedMobileMode }),
            ...(transactionRef && { transactionRef }),
            ...(notes && { notes }),
        };

        onAddPayment(newPayment);
        resetForm();
    }, [canAddPayment, selectedMethode, amount, isMobilePayment, selectedMobileMode, transactionRef, notes, onAddPayment, resetForm]);

    // Payer le montant restant avec une seule méthode
    const handlePayFullAmount = useCallback(() => {
        if (!selectedMethode) return;
        if (isMobilePayment && !selectedMobileMode) return;

        const fullPayment: MultiplePaymentMethod = {
            methode: selectedMethode,
            amount: totalToPay,
            ...(isMobilePayment && selectedMobileMode && { mobilePayment: selectedMobileMode }),
            ...(transactionRef && { transactionRef }),
            ...(notes && { notes }),
        };

        // Supprimer tous les paiements existants et ajouter celui-ci
        paymentMethods.forEach((_, index) => onRemovePayment(0));
        onAddPayment(fullPayment);
        resetForm();
    }, [selectedMethode, isMobilePayment, selectedMobileMode, totalToPay, transactionRef, notes, paymentMethods, onRemovePayment, onAddPayment, resetForm]);

    // Remplir le montant restant
    const fillRemainingAmount = useCallback(() => {
        setAmount(remainingAmount);
    }, [remainingAmount]);

    const renderMethodeCard = useCallback((
        label: string,
        isSelected: boolean,
        onClick: () => void,
        methode: PaymentMethodeEnum,
    ) => (
        <div
            key={label}
            onClick={onClick}
            className={`cursor-pointer border rounded-lg px-3 py-2 text-xs sm:text-sm font-medium transition-all flex items-center gap-2
                ${isSelected ? 'bg-primary text-white border-primary' : 'bg-white hover:bg-gray-100 text-gray-600'}`}
        >
            {getPaymentIcon(methode)}
            {label}
        </div>
    ), []);

    const renderMobileModeCard = useCallback((
        label: string,
        isSelected: boolean,
        onClick: () => void,
    ) => (
        <div
            key={label}
            onClick={onClick}
            className={`cursor-pointer border rounded-lg px-3 py-2 text-xs font-medium transition-all
                ${isSelected ? 'bg-orange-500 text-white border-orange-500' : 'bg-white hover:bg-gray-100 text-gray-600'}`}
        >
            {label}
        </div>
    ), []);

    return (
        <div className="space-y-6">
            {/* Résumé des paiements */}
            <div className="bg-gradient-to-r from-primary/10 to-secondary/10 rounded-xl p-4">
                <div className="flex justify-between items-center mb-2">
                    <span className="text-gray-600 font-medium">Total à payer</span>
                    <span className="text-lg font-bold">{formatPrice(totalToPay)}</span>
                </div>
                <div className="flex justify-between items-center mb-2">
                    <span className="text-gray-600">Déjà payé</span>
                    <span className="text-green-600 font-semibold">{formatPrice(totalPaid)}</span>
                </div>
                <div className="border-t border-gray-200 pt-2 mt-2">
                    <div className="flex justify-between items-center">
                        <span className="text-gray-800 font-semibold">Reste à payer</span>
                        <span className={`text-lg font-bold ${remainingAmount > 0 ? 'text-red-600' : 'text-green-600'}`}>
                            {formatPrice(remainingAmount)}
                        </span>
                    </div>
                </div>

                {/* Barre de progression */}
                <div className="mt-3">
                    <div className="w-full bg-gray-200 rounded-full h-2">
                        <div
                            className="bg-green-500 h-2 rounded-full transition-all duration-300"
                            style={{ width: `${Math.min(100, (totalPaid / totalToPay) * 100)}%` }}
                        />
                    </div>
                    <p className="text-xs text-gray-500 mt-1 text-right">
                        {Math.round((totalPaid / totalToPay) * 100)}% payé
                    </p>
                </div>
            </div>

            {/* Liste des paiements ajoutés */}
            {paymentMethods.length > 0 && (
                <div className="space-y-2">
                    <h4 className="font-semibold text-gray-700 text-sm">Paiements ajoutés</h4>
                    {paymentMethods.map((payment, index) => (
                        <div
                            key={index}
                            className={`flex items-center justify-between p-3 rounded-lg border ${getPaymentMethodColor(payment.methode)}`}
                        >
                            <div className="flex items-center gap-3">
                                {getPaymentIcon(payment.methode)}
                                <div>
                                    <p className="font-medium text-sm">
                                        {payment.methode}
                                        {payment.mobilePayment && ` (${payment.mobilePayment})`}
                                    </p>
                                    {payment.transactionRef && (
                                        <p className="text-xs opacity-75">Réf: {payment.transactionRef}</p>
                                    )}
                                </div>
                            </div>
                            <div className="flex items-center gap-3">
                                <span className="font-bold">{formatPrice(payment.amount)}</span>
                                <button
                                    onClick={() => onRemovePayment(index)}
                                    className="p-1 hover:bg-white/50 rounded-full transition-colors"
                                >
                                    <Trash2 className="w-4 h-4" />
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* Formulaire d'ajout de paiement */}
            {remainingAmount > 0 && (
                <div className="border rounded-xl p-4 bg-gray-50">
                    <h4 className="font-semibold text-gray-700 mb-4 flex items-center gap-2">
                        <Plus className="w-4 h-4" />
                        Ajouter un paiement
                    </h4>

                    {/* Sélection de la méthode */}
                    <div className="mb-4">
                        <p className="text-sm text-gray-600 mb-2">Méthode de paiement</p>
                        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                            {modesToShow.map(([key, value]) =>
                                renderMethodeCard(
                                    value,
                                    selectedMethode === value,
                                    () => {
                                        setSelectedMethode(value as PaymentMethodeEnum);
                                        setSelectedMobileMode(null);
                                    },
                                    value as PaymentMethodeEnum
                                )
                            )}
                        </div>
                    </div>

                    {/* Sélection de l'opérateur mobile */}
                    {isMobilePayment && (
                        <div className="mb-4">
                            <p className="text-sm text-gray-600 mb-2">Opérateur mobile</p>
                            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                                {mobileModes.map(([key, value]) =>
                                    renderMobileModeCard(
                                        value,
                                        selectedMobileMode === value,
                                        () => setSelectedMobileMode(value as MobilePaymentEnum)
                                    )
                                )}
                            </div>
                        </div>
                    )}

                    {/* Info crédit */}
                    {isCreditPayment && selectedClient && (
                        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                            <p className="text-sm font-medium text-red-800 mb-1">Information crédit</p>
                            <p className="text-xs text-red-600">
                                Client: {selectedClient.firstname} {selectedClient.lastname}
                            </p>
                            <p className="text-xs text-red-600">
                                {selectedClient.phoneNumber}
                            </p>
                        </div>
                    )}

                    {/* Montant et référence */}
                    {selectedMethode && (
                        <div className="space-y-3">
                            <div className="flex gap-2 items-end">
                                <div className="flex-1">
                                    <InputWrapper label="Montant">
                                        <Input
                                            type="number"
                                            min={0}
                                            max={remainingAmount}
                                            placeholder="0"
                                            value={amount || ''}
                                            onChange={(e) => setAmount(Number(e.target.value))}
                                        />
                                    </InputWrapper>
                                </div>
                                <Button
                                    variant="info"
                                    className="text-xs px-3 py-2 h-[42px]"
                                    onClick={fillRemainingAmount}
                                >
                                    Reste ({formatPrice(remainingAmount)})
                                </Button>
                            </div>

                            {(isMobilePayment || selectedMethode === PaymentMethodeEnum.cheque) && (
                                <InputWrapper label="Référence transaction (optionnel)">
                                    <Input
                                        type="text"
                                        placeholder="ex: TXN123456"
                                        value={transactionRef}
                                        onChange={(e) => setTransactionRef(e.target.value)}
                                    />
                                </InputWrapper>
                            )}

                            <InputWrapper label="Notes (optionnel)">
                                <Input
                                    type="text"
                                    placeholder="ex: Paiement partiel"
                                    value={notes}
                                    onChange={(e) => setNotes(e.target.value)}
                                />
                            </InputWrapper>

                            {/* Boutons d'action */}
                            <div className="flex gap-2 pt-2">
                                {paymentMethods.length === 0 && (
                                    <Button
                                        variant="secondary"
                                        className="flex-1 text-sm"
                                        onClick={handlePayFullAmount}
                                        disabled={!selectedMethode || (isMobilePayment && !selectedMobileMode)}
                                    >
                                        Payer tout ({formatPrice(totalToPay)})
                                    </Button>
                                )}
                                <Button
                                    variant="primary"
                                    className="flex-1 text-sm"
                                    onClick={handleAddPayment}
                                    disabled={!canAddPayment}
                                >
                                    <Plus className="w-4 h-4 mr-1" />
                                    Ajouter ce paiement
                                </Button>
                            </div>
                        </div>
                    )}
                </div>
            )}

            {/* Message de paiement complet */}
            {remainingAmount === 0 && paymentMethods.length > 0 && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-center">
                    <p className="text-green-800 font-semibold">
                        ✅ Paiement complet !
                    </p>
                    <p className="text-green-600 text-sm">
                        Le montant total a été couvert par {paymentMethods.length} paiement{paymentMethods.length > 1 ? 's' : ''}.
                    </p>
                </div>
            )}
        </div>
    );
};
